<template>
  <div class="container-fluid p-0">
    <div v-if="currentOrder">
      <ScanInput ref="itemScanInput" :instruction="$t('action.scanItem')" @scanned="itemCodeScanned" :hidden="true" />
      <div class="d-flex gap-3 align-items-center px-0">
        <RouterLink :to="TranslationsService.i18nRoute({ name: 'receivings' })" class="btn btn-outline-secondary rounded-start-0" active-class="">
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
          {{ $t('global.list') }}
        </RouterLink>
        <div>
          <span class="text-primary fw-bold fs-3">{{ currentOrder.code }}</span><br>
          <span class="text-melodi">{{ currentSupplier?.name }}</span><br>
        </div>
        <div>
          <OrderStatus :status="currentOrder.status" :show-text="true" class="me-2" />
          <button class="btn btn-primary mt-1" type="button" :disabled="uiList.length === 0 || !formIsValid" @click="completeOrder()">
            <div v-if="isSaving" class="spinner-border spinner-border-sm"></div>
            <font-awesome-icon v-else :icon="['fas', 'check']" />
            {{ $t('action.complete') }}
          </button>
        </div>
        <div class="text-truncate d-none d-xxl-block">
          {{ $t('field.generic.created') }}<br>
          <AuditItem :at="currentOrder.audit.createdAt" :by="currentOrder.audit.createdBy" />
        </div>
        <div v-if="currentOrder.audit.createdAt !== currentOrder.audit.updatedAt" class="d-none d-xxl-block">
          {{ $t('field.generic.updated') }}<br>
          <AuditItem :at="currentOrder.audit.updatedAt" :by="currentOrder.audit.updatedBy" />
        </div>
      </div>

      <div v-if="uiList.length !== 0" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
        <table class="table table-hover table-sm table-fit align-middle">
          <thead>
            <!-- Show on large screen -->
            <tr class="d-none d-sm-table-row">
              <th class="mel-bar"></th>
              <th>
                {{ $t('field.generic.item') }}
                <font-awesome-icon :icon="['fas', 'sort-up']" />
              </th>
              <th>{{ $t('field.order.orderedQuantity') }}</th>
              <th>{{ $t('field.receiving.deliveredQuantity') }}</th>
              <th>{{ $t('field.masterdata.lot') }}</th>
              <th v-if="hasBestBeforeOnAnyLine">{{ $t('field.masterdata.bestBefore') }}</th>
              <th v-if="hasExpiryOnAnyLine">{{ $t('field.masterdata.expiry') }}</th>
            </tr>
            <!-- Show on small screen -->
            <tr class="d-table-row d-sm-none">
              <th class="mel-bar"></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="entry in uiList" :key="entry.id">
              <template v-if="entry.isEditing">
                <!-- Show on large screen -->
                <tr class="d-none d-sm-table-row" :class="{ 'row-selected': entry.isEditing, 'is-filled-danger': entry.isMissing, 'is-filled-warning': entry.isUnderDelivered, 'is-filled-info': entry.isOverDelivered, 'is-filled-success-NONE': entry.isExactDelivered }">
                  <td class="mel-bar" :class="{ 'mel-bar-danger': entry.isMissing, 'mel-bar-warning': entry.isUnderDelivered, 'mel-bar-info': entry.isOverDelivered, 'mel-bar-success-NONE': entry.isExactDelivered }"></td>
                  <td>
                    <div class="mel-absolute-warning-container">
                      <div v-if="!entry.isValid" class="text-warning fw-bold mel-absolute-warning-background"><font-awesome-icon :icon="['fas', 'triangle-exclamation']" size="2x" opacity="0.4" /></div>
                      <div class="mel-absolute-warning-foreground"><ItemField :item="entry.item" /></div>
                    </div>
                  </td>
                  <td><QuantityField :quantity="entry.quantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" /></td>
                  <td><QuantityField :quantity="entry.deliveredQuantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" :open-input="true" :min-quantity="0" :field-id="'LG' + entry.id" v-on:update:quantity="updateDeliveredQuantity(entry, $event)" /></td>
                  <td><input v-if="entry.trackStock && entry.item.trackLotNumber" v-model="entry.lot" type="text" class="form-control" @input="convertToUpperCase" maxlength="20"></td>
                  <td v-if="hasBestBeforeOnAnyLine"><input v-if="entry.trackStock && entry.item.trackBestBeforeDate" v-model="entry.expiry" type="date" class="form-control"></td>
                  <td v-if="hasExpiryOnAnyLine"><input v-if="entry.trackStock && entry.item.trackExpirationDate" v-model="entry.expiry" type="date" class="form-control"></td>
                </tr>
                <!-- Show on small screen (row 1/2) -->
                <tr class="d-table-row d-sm-none border-bottom-0" :class="{ 'row-selected': entry.isEditing, 'is-filled-danger': entry.isMissing, 'is-filled-warning': entry.isUnderDelivered, 'is-filled-info': entry.isOverDelivered, 'is-filled-success-NONE': entry.isExactDelivered }">
                  <td class="mel-bar" :class="{ 'mel-bar-danger': entry.isMissing, 'mel-bar-warning': entry.isUnderDelivered, 'mel-bar-info': entry.isOverDelivered, 'mel-bar-success-NONE': entry.isExactDelivered }"></td>
                  <td>
                    <div class="mel-absolute-warning-container">
                      <div v-if="!entry.isValid" class="text-warning fw-bold mel-absolute-warning-background"><font-awesome-icon :icon="['fas', 'triangle-exclamation']" size="2x" opacity="0.4" /></div>
                      <div class="mel-absolute-warning-foreground"><ItemField :item="entry.item" /></div>
                    </div>
                  </td>
                  <td><QuantityField :quantity="entry.quantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" /></td>
                </tr>
                <!-- Show on small screen (row 2/2) -->
                <tr class="d-table-row d-sm-none" :class="{ 'row-selected': entry.isEditing, 'is-filled-danger': entry.isMissing, 'is-filled-warning': entry.isUnderDelivered, 'is-filled-info': entry.isOverDelivered, 'is-filled-success-NONE': entry.isExactDelivered }">
                  <td class="mel-bar" :class="{ 'mel-bar-danger': entry.isMissing, 'mel-bar-warning': entry.isUnderDelivered, 'mel-bar-info': entry.isOverDelivered, 'mel-bar-success-NONE': entry.isExactDelivered }"></td>
                  <td colspan="2">
                    <table>
                      <tbody>
                        <tr>
                          <td class="text-end"><span class="small me-2">{{ $t('field.generic.quantity') }}</span></td>
                          <td><QuantityField :quantity="entry.deliveredQuantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" :open-input="true" :min-quantity="0" :field-id="'SM' + entry.id" v-on:update:quantity="updateDeliveredQuantity(entry, $event)" /></td>
                        </tr>
                        <tr v-if="entry.trackStock && entry.item.trackLotNumber">
                          <td class="text-end"><span class="small me-2">{{ $t('field.masterdata.lot') }}</span></td>
                          <td><input v-model="entry.lot" type="text" class="form-control" @input="convertToUpperCase" maxlength="20"></td>
                        </tr>
                        <tr v-if="entry.trackStock && entry.item.trackBestBeforeDate">
                          <td class="text-end"><span class="small me-2">{{ $t('field.masterdata.bestBefore') }}</span></td>
                          <td><input v-model="entry.bestBefore" type="date" class="form-control"></td>
                        </tr>
                        <tr v-if="entry.trackStock && entry.item.trackExpirationDate">
                          <td class="text-end"><span class="small me-2">{{ $t('field.masterdata.expiry') }}</span></td>
                          <td><input v-model="entry.expiry" type="date" class="form-control"></td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
              <template v-else>
                <!-- Show on large screen -->
                <tr @click="selectRow(entry)" class="d-none d-sm-table-row" :class="{ 'row-selected': entry.isEditing, 'is-filled-danger': entry.isMissing, 'is-filled-warning': entry.isUnderDelivered, 'is-filled-info': entry.isOverDelivered, 'is-filled-success-NONE': entry.isExactDelivered }">
                  <td class="mel-bar" :class="{ 'mel-bar-danger': entry.isMissing, 'mel-bar-warning': entry.isUnderDelivered, 'mel-bar-info': entry.isOverDelivered, 'mel-bar-success-NONE': entry.isExactDelivered }"></td>
                  <td>
                    <div class="mel-absolute-warning-container">
                      <div v-if="!entry.isValid" class="text-warning fw-bold mel-absolute-warning-background"><font-awesome-icon :icon="['fas', 'triangle-exclamation']" size="2x" opacity="0.4" /></div>
                      <div class="mel-absolute-warning-foreground"><ItemField :item="entry.item" /></div>
                    </div>
                  </td>
                  <td><QuantityField :quantity="entry.quantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" /></td>
                  <td><QuantityField :quantity="entry.deliveredQuantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" :open-input="true" :min-quantity="0" v-on:update:quantity="updateDeliveredQuantity(entry, $event)" /></td>
                  <td><LotField v-if="entry.trackStock && entry.item.trackLotNumber" :item="entry.item" :lot="entry.lot" :warn-empty="true" /></td>
                  <td v-if="hasBestBeforeOnAnyLine">
                    <span v-if="entry.trackStock && entry.item.trackBestBeforeDate && entry.bestBefore">{{ $d(new Date(entry.bestBefore), 'dateonly') }}</span>
                    <span v-if="entry.trackStock && entry.item.trackBestBeforeDate && !entry.bestBefore" class="text-warning"><font-awesome-icon :icon="['fas', 'square-dashed']" /></span>
                  </td>
                  <td v-if="hasExpiryOnAnyLine">
                    <span v-if="entry.trackStock && entry.item.trackExpirationDate && entry.expiry">{{ $d(new Date(entry.expiry), 'dateonly') }}</span>
                    <span v-if="entry.trackStock && entry.item.trackExpirationDate && !entry.expiry" class="text-warning"><font-awesome-icon :icon="['fas', 'square-dashed']" /></span>
                  </td>
                </tr>
                <!-- Show on small screen -->
                <tr @click="selectRow(entry)" class="d-table-row d-sm-none" :class="{ 'row-selected': entry.isEditing, 'is-filled-danger': entry.isMissing, 'is-filled-warning': entry.isUnderDelivered, 'is-filled-info': entry.isOverDelivered, 'is-filled-success-NONE': entry.isExactDelivered }">
                  <td class="mel-bar" :class="{ 'mel-bar-danger': entry.isMissing, 'mel-bar-warning': entry.isUnderDelivered, 'mel-bar-info': entry.isOverDelivered, 'mel-bar-success-NONE': entry.isExactDelivered }"></td>
                  <td>
                    <div class="mel-absolute-warning-container">
                      <div v-if="!entry.isValid" class="text-warning fw-bold mel-absolute-warning-background"><font-awesome-icon :icon="['fas', 'triangle-exclamation']" size="2x" opacity="0.4" /></div>
                      <div class="mel-absolute-warning-foreground"><ItemField :item="entry.item" /></div>
                    </div>
                  </td>
                  <td><QuantityField :quantity="entry.quantity" :unit-name="entry.unitName" :pad-length="maxQuantityLength" /></td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else>
        <slot name="empty-list" />
      </div>

    </div>
    <div v-else>
      <span >{{ $t('error.noData') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue';
import { Id, toast, type ToastOptions } from 'vue3-toastify';
import TranslationsService from "@/services/translations";
import { useI18n } from 'vue-i18n';
import { v4 as uuidv4 } from "uuid";
import { useForeignItemsStore, useItemsStore, useOrdersStore, useReplacementItemsStore, useSuppliersStore, useTradeItemsStore } from '@/stores';
import { ItemField, QuantityField } from '@/components/fields';
import AuditItem from "@/components/AuditItem.vue";
import ScanInput from '@/components/form/ScanInput.vue';
import OrderStatus from '@/components/orders/OrderStatus.vue';
import CompleteOrderRequest, { CompleteOrderLineRequest } from '@/models/requests/CompleteOrderRequest';
import Item from '@/models/frontend/Item';
import Supplier from '@/models/frontend/Supplier';
import Order from '@/models/frontend/Order';
import { router } from '@/services';
import IItemWithSignatureRaw from '@/models/IItemWithSignatureRaw';
import IGS1 from '@/models/IGS1';
import IForeignItem from '@/models/responses/IForeignItem';
import LotField from '@/components/fields/LotField.vue';

const i18n = useI18n();

const itemsStore = useItemsStore();
const tradeItemsStore = useTradeItemsStore();
const ordersStore = useOrdersStore();
const suppliersStore = useSuppliersStore();
const replacementItemsStore = useReplacementItemsStore();
const foreignItemsStore = useForeignItemsStore();

const props = defineProps({
  orderId: { type: Number, required: true }
});

const currentOrder = ref<Order | undefined>(ordersStore.getById(props.orderId));
const currentSupplier = ref<Supplier | undefined>(undefined);
const isSaving = ref(false);
const toastId = ref('' as Id);
const uiList = ref([] as SelectableItem[]);
const offsetTop = ref(0);

class SelectableItem implements IItemWithSignatureRaw {
  public id: string = uuidv4();
  public createTimestamp = Date.now();
  public highlight = false;
  public deliveredQuantity: number;
  public lot: string | undefined;
  public bestBefore: string | undefined;
  public expiry: string | undefined;
  public isEditing = false;
  constructor(
    public item: Item,
    public foreignItem: IForeignItem | undefined = undefined,
    public unitCode: string | null,
    public quantity: number,
    public factor: number,
    public baseUnitCode: string
  ) {
    this.deliveredQuantity = this.quantity;
  }

  get unitName() {
    if (this.unitCode === this.baseUnitCode) {
      return this.unitCode;
    }
    return `${this.unitCode} à ${this.factor} ${this.baseUnitCode}`;
  }

  get isExactDelivered() {
    return this.deliveredQuantity === this.quantity;
  }
  get isMissing() {
    return this.deliveredQuantity === 0;
  }
  get isUnderDelivered() {
    return this.deliveredQuantity < this.quantity && this.deliveredQuantity > 0;
  }
  get isOverDelivered() {
    return this.deliveredQuantity > this.quantity;
  }
  get trackStock() {
    if (this.isMissing) return false;
    return this.foreignItem?.trackStock ?? true;
  }

  get isValid() {
    if (this.deliveredQuantity < 0) return false;
    if (this.trackStock && this.item.trackLotNumber && !this.lot) return false;
    if (this.trackStock && this.item.trackBestBeforeDate && !this.bestBefore) return false;
    if (this.trackStock && this.item.trackExpirationDate && !this.expiry) return false;
    return true;
  }
}

onMounted(async () => {
  await ordersStore.fetch(props.orderId);
  currentOrder.value = ordersStore.getById(props.orderId);
  if (currentOrder.value == undefined) {
    toast.error(i18n.t('page.order.notFoundError'));
    return;
  }
  currentSupplier.value = suppliersStore.getById(currentOrder.value.supplier.id);
  if (currentSupplier.value == undefined) {
    toast.error(i18n.t('page.order.supplierNotFoundError'));
    return;
  }

  const mappedLines = currentOrder.value.lines.map((line) => constructItem(itemsStore.getById(line.item.id), line.unitCode, line.quantity));
  uiList.value = [...mappedLines].sort((a, b) =>
    a.item.name.localeCompare(b.item.name)
  );
  if (uiList.value.length > 0) {
    selectRow(uiList.value[0]!); // select first item
  }
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const hasBestBeforeOnAnyLine = computed(() => {
  return uiList.value !== undefined && uiList.value.findIndex((x) => !!x.item.trackBestBeforeDate) !== -1;
});

const hasExpiryOnAnyLine = computed(() => {
  return uiList.value !== undefined && uiList.value.findIndex((x) => !!x.item.trackExpirationDate) !== -1;
});

const maxQuantityLength = computed(() => {
  return uiList.value.reduce((acc, entry) => Math.max(acc, entry.quantity.toString().length), 0);
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const formIsValid = computed(() => uiList.value.every((entry) => entry.isValid));

const selectRow = (selectedItem: SelectableItem) => {
  uiList.value.forEach(entry => {
    entry.isEditing = entry === selectedItem;

    if (entry.isEditing) {
      nextTick(() => {
        const lgInput = document.querySelector(`input[data-field-id="LG${entry.id}"]`) as HTMLInputElement;
        if (lgInput && isElementVisible(lgInput)) {
          lgInput.focus();
          lgInput.select();
        } else {
          const smInput = document.querySelector(`input[data-field-id="SM${entry.id}"]`) as HTMLInputElement;
          if (smInput && isElementVisible(smInput)) {
            smInput.focus();
            smInput.select();
          }
        }
      });
    }
  });
};

const isElementVisible = (el: HTMLInputElement) => {
  return !!(el && 
            el.offsetParent !== null && // checks display and visibility
            window.getComputedStyle(el).visibility !== 'hidden' &&
            window.getComputedStyle(el).display !== 'none');
}

const completeOrder = async () => {
  if (currentOrder.value === undefined || uiList.value.length === 0) {
    toast.warn(i18n.t('error.nothingToSave'), { autoClose: 10000, theme: 'colored' });
    return;
  }
  isSaving.value = true;
  toastId.value = toast.loading(i18n.t('page.order-create.orderSaving'), {
    autoClose: false,
    theme: 'colored'
  } as ToastOptions);
  const orderLines = uiList.value
    .map((entry) => (new CompleteOrderLineRequest(entry.item.id, entry.deliveredQuantity, entry.lot, entry.bestBefore, entry.expiry)))
    .sort((a, b) => a.itemId - b.itemId); // sort by itemId to ensure consistent order
  const orderRequest = new CompleteOrderRequest(orderLines);
  await ordersStore.complete(currentOrder.value.id, orderRequest, onApiFulfilled, onApiRejected);
};

function onApiFulfilled(): void | PromiseLike<void> {
  isSaving.value = false;
  toast.update(toastId.value, {
    render: i18n.t('statement.receivingIsAccepted'),
    type: toast.TYPE.SUCCESS,
    isLoading: false,
    autoClose: 10000,
    closeOnClick: true
  } as ToastOptions);

  setTimeout(() => {
    if (currentOrder.value !== undefined) {
      router.push({ name: 'order', params: { orderId: currentOrder.value.id } });
    }
  }, 3000);
}

function onApiRejected(reason: any): void | PromiseLike<void> {
  isSaving.value = false;
  toast.update(toastId.value, {
    render: i18n.t('error.apiError', { error: reason }),
    closeOnClick: true,
    type: toast.TYPE.ERROR,
    isLoading: false,
    autoClose: 10000,
    theme: 'colored'
  } as ToastOptions);
}

const itemCodeScanned = async (barcode: string, gs1: IGS1 | undefined) => {
  if (currentOrder.value === undefined) return;
  if (!barcode && !gs1) return;

  const contentCode = gs1?.gtin ?? barcode;
  const tradeItem = tradeItemsStore.getByCode(contentCode);
  if (tradeItem) {
    // const item = itemsStore.getById(tradeItem.itemId);
    const entry = uiList.value.find((x) => x.item.id === tradeItem.itemId);
    if (!entry) return;

    selectRow(entry);

    if (!entry.trackStock) return;

    if (entry.item.trackLotNumber && gs1?.lot) {
      entry.lot = gs1.lot;
    }

    if (entry.item.trackBestBeforeDate && gs1?.bestBefore) {
      entry.bestBefore = getDateString(gs1.bestBefore);
    }

    if (entry.item.trackExpirationDate && gs1?.expiry) {
      entry.expiry = getDateString(gs1.expiry);
    }
  }
};

const getDateString = (date: Date | undefined) => {
  if (!date) { return undefined; }
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - (offset * 60 * 1000));
  return date.toISOString().split('T')[0];
}

const constructItem = (item: Item, orderUnitCode: string, quantity: number) => {
  const baseItemId = replacementItemsStore.getBaseItem(item.id);
  if (baseItemId) item = itemsStore.getById(baseItemId);

  const tradeItem = tradeItemsStore.getByItemIdAndUnitCode(item.id, orderUnitCode);
  const foreignItem = foreignItemsStore.getByItemId(item.id);
  if (tradeItem === undefined) {
    return new SelectableItem(item, foreignItem, orderUnitCode, quantity, 1, orderUnitCode);
  }
  else {
    return new SelectableItem(item, foreignItem, orderUnitCode, quantity, tradeItem.factor, item.baseUnitCode);
  }
};

const updateDeliveredQuantity = (item: SelectableItem, quantity: number) => {
  item.deliveredQuantity = quantity;
};

const convertToUpperCase = (event: Event) => {
  const input = event.target as HTMLInputElement;
  input.value = input.value.toUpperCase();
}
</script>

<style lang="scss" scoped>
tr.row-selected > td {
  background-color: var(--mel-row-filled-bg);
}

tr.is-filled-primary > td {
  background-color: var(--bs-primary-bg-subtle);
}
tr.is-filled-secondary > td {
  background-color: var(--bs-secondary-bg-subtle);
}
tr.is-filled-success > td {
  background-color: var(--bs-success-bg-subtle);
}
tr.is-filled-warning > td {
  background-color: var(--bs-warning-bg-subtle);
}
tr.is-filled-danger > td {
  background-color: var(--bs-danger-bg-subtle);
}
tr.is-filled-info > td {
  background-color: var(--bs-info-bg-subtle);
}

table td.mel-bar,
table th.mel-bar
{
  padding-left: 0;
  padding-right: 0;
  width: 6px;
  min-width: 6px;
  max-width: 6px;
}

.table > :not(caption) > * > .mel-bar-none,
.mel-bar-none {
  background-color: transparent;
}
.table > :not(caption) > * > .mel-bar-secondary,
.mel-bar-secondary {
  background-color: var(--bs-body-bg);
}
.table > :not(caption) > * > .mel-bar-primary,
.mel-bar-primary {
  background-color: var(--bs-primary);
}
.table > :not(caption) > * > .mel-bar-success,
.mel-bar-success {
  background-color: var(--bs-success);
}
.table > :not(caption) > * > .mel-bar-warning,
.mel-bar-warning {
  background-color: var(--bs-warning);
}
.table > :not(caption) > * > .mel-bar-danger,
.mel-bar-danger {
  background-color: var(--bs-danger);
}
.table > :not(caption) > * > .mel-bar-info,
.mel-bar-info {
  background-color: var(--bs-info);
}

.table > :not(caption) > *.border-bottom-0 > * {
  border-bottom-width: 0;
}

.mel-absolute-warning-container {
  position: relative; /* Acts as the reference point */
  overflow: hidden;
}
.mel-absolute-warning-background {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(0, -50%);
  z-index: 0;
}
.mel-absolute-warning-foreground {
  position: relative; /* Keeps it above the background */
  z-index: 1;
}
</style>
